/**
 * SheetChrome.jsx — press-room chrome for the sheet hero (PR ⑦).
 *
 * Ported from the Claude Design prototype (`mono-vector.jsx`): the dot-grid
 * backdrop, dimension rulers, corner crop marks and the CMYK colour bar that
 * make the canvas read as a press sheet rather than a generic diagram.
 *
 * ── Coordinate spaces (the thing to get right) ─────────────────────────────
 * The sheet itself is drawn in MILLIMETRE space inside a
 * `<g transform="translate(pan) scale(zoom)">`, so 1 user unit = 1 mm.
 *
 * Rulers, crop marks and the colour bar are drawn in SCREEN space instead, as
 * an absolutely-positioned overlay. That is deliberate: their tick lengths,
 * label sizes and mark offsets are fixed pixel affordances — a 10 px crop mark
 * must stay 10 px at every zoom level, not scale with the sheet. Screen
 * position is therefore computed as `origin + mm * scale`, where
 * `origin = pan` and `scale = zoom` from useCanvasControls.
 *
 * The overlay is `pointerEvents: none` throughout so it can never intercept a
 * pan drag on the canvas beneath it.
 */

import React from 'react';
import { useTheme, MM_PER_IN } from './theme';

/** Dot-grid backdrop. Pure CSS-space pattern — independent of pan/zoom. */
export function DotGrid({ id = 'sheet-dotgrid' }) {
  const { P } = useTheme();
  return (
    <svg width="100%" height="100%" aria-hidden
      style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none' }}>
      <defs>
        <pattern id={id} width="16" height="16" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.8" fill={P.rule} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

/**
 * Choose a tick interval that stays legible at the current zoom.
 * Returned in DISPLAY units; the caller converts back to mm for positioning.
 */
export function tickStep(unit, scale) {
  if (unit === 'in') return scale > 2.4 ? 0.5 : scale > 1.2 ? 1 : 2;
  return scale > 1.5 ? 10 : scale > 0.7 ? 25 : 50;
}

/**
 * Dimension rulers along the top and left edges.
 *
 * Ticks are placed at round numbers in the ACTIVE DISPLAY UNIT (so an
 * inch-based shop sees 0/1/2 in, not 0/25.4/50.8), but positioned by converting
 * each back to mm — the canonical space the sheet is drawn in. Every other tick
 * is major and carries a label.
 */
export function Rulers({ ox, oy, sheetW, sheetH, scale }) {
  const { P, MO, unit } = useTheme();
  if (!(sheetW > 0) || !(sheetH > 0) || !(scale > 0)) return null;

  const stepU = tickStep(unit, scale);
  const stepMm = unit === 'in' ? stepU * MM_PER_IN : stepU;
  if (!(stepMm > 0)) return null;

  // Guard against a pathological zoom producing thousands of nodes.
  const MAX_TICKS = 400;
  const ticks = (extentMm) => {
    const out = [];
    for (let mm = 0; mm <= extentMm + 1e-6 && out.length < MAX_TICKS; mm += stepMm) out.push(mm);
    return out;
  };

  const label = (mm) => {
    if (unit !== 'in') return String(Math.round(mm));
    const v = mm / MM_PER_IN;
    return v.toFixed(stepU < 1 ? 1 : 0);
  };

  return (
    <>
      {ticks(sheetW).map((mm, i) => {
        const x = ox + mm * scale;
        const major = i % 2 === 0;
        return (
          <g key={`x${i}`}>
            <line x1={x} y1={oy - 10} x2={x} y2={oy - (major ? 16 : 13)} stroke={P.muted} strokeWidth={0.5} />
            {major && (
              <text x={x} y={oy - 20} textAnchor="middle" fontFamily={MO} fontSize={8.5} fill={P.dim} letterSpacing="0.4">
                {label(mm)}
              </text>
            )}
          </g>
        );
      })}
      {ticks(sheetH).map((mm, i) => {
        const y = oy + mm * scale;
        const major = i % 2 === 0;
        return (
          <g key={`y${i}`}>
            <line x1={ox - 10} y1={y} x2={ox - (major ? 16 : 13)} y2={y} stroke={P.muted} strokeWidth={0.5} />
            {major && (
              <text x={ox - 20} y={y + 3} textAnchor="end" fontFamily={MO} fontSize={8.5} fill={P.dim} letterSpacing="0.4">
                {label(mm)}
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}

/**
 * Corner crop marks — the trim registration a prepress operator expects.
 * Fixed pixel geometry: they sit `off` px clear of the corner and run `len` px
 * outward, at every zoom level.
 */
export function CropMarks({ ox, oy, w, h }) {
  const { P } = useTheme();
  const len = 10, off = 6;
  const corners = [[ox, oy], [ox + w, oy], [ox, oy + h], [ox + w, oy + h]];
  const lines = [];
  corners.forEach(([cx, cy], i) => {
    const sx = i % 2 === 0 ? -1 : 1;   // left column vs right
    const sy = i < 2 ? -1 : 1;         // top row vs bottom
    lines.push([cx + sx * off, cy, cx + sx * (off + len), cy]);
    lines.push([cx, cy + sy * off, cx, cy + sy * (off + len)]);
  });
  return (
    <g>
      {lines.map((l, i) => (
        <line key={i} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]} stroke={P.ink} strokeWidth={0.5} />
      ))}
    </g>
  );
}

/**
 * CMYK + spot colour bar.
 *
 * The four process swatches are fixed reference inks and are intentionally NOT
 * theme tokens — cyan must look like cyan in dark mode too. Only the spot
 * swatch follows the active accent.
 */
export function ColorBar({ x, y }) {
  const { P, MO } = useTheme();
  const sw = 10, h = 10;
  const inks = ['#00AEEF', '#EC008C', '#FFF200', '#0A0A0A', P.accent];
  return (
    <g transform={`translate(${x} ${y})`}>
      {inks.map((c, i) => (
        <rect key={i} x={i * (sw + 2)} y={0} width={sw} height={h} fill={c} stroke={P.ink} strokeWidth={0.25} />
      ))}
      <text x={inks.length * (sw + 2) + 6} y={h - 1.5} fontFamily={MO} fontSize={8.5} fill={P.dim} letterSpacing="0.6">
        CMYK + 1 SPOT
      </text>
    </g>
  );
}

/**
 * The whole screen-space overlay in one component.
 *
 * @param ox,oy   sheet origin in screen px (= pan)
 * @param scale   px per mm (= zoom)
 * @param sheetW,sheetH  sheet size in mm
 */
export default function SheetChrome({ ox, oy, scale, sheetW, sheetH }) {
  if (!(sheetW > 0) || !(sheetH > 0) || !(scale > 0)) return null;
  const w = sheetW * scale;
  const h = sheetH * scale;
  return (
    <svg width="100%" height="100%" aria-hidden
      style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none' }}>
      <Rulers ox={ox} oy={oy} sheetW={sheetW} sheetH={sheetH} scale={scale} />
      <CropMarks ox={ox} oy={oy} w={w} h={h} />
      <ColorBar x={ox} y={oy + h + 14} />
    </svg>
  );
}
